import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';

// Frozen so a caller that accidentally mutates these doesn't poison future
// "empty" states for every other caller of the hook.
const EMPTY_GROUPS = Object.freeze({});
const EMPTY_COLUMNS = Object.freeze({});

export default function useGroupedBoard({
  contactEmail,
  linkedOrderId,
  statuses,
  enabled = true,
}) {
  const [groups, setGroups] = useState(EMPTY_GROUPS);
  const [columns, setColumns] = useState(EMPTY_COLUMNS);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);

  const requestIdRef = useRef(0);

  const fetcher = useCallback(async () => {
    if (!enabled) {
      setGroups(EMPTY_GROUPS);
      setColumns(EMPTY_COLUMNS);
      setLoading(false);
      setError(null);
      return;
    }

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (contactEmail) params.contactEmail = contactEmail;
      if (linkedOrderId) params.linkedOrderId = linkedOrderId;
      if (statuses) params.statuses = statuses;

      const response = await api.get('/auth/slicer-quote/board-grouped', { params });
      if (reqId !== requestIdRef.current) return;

      const data = response.data?.data || response.data || {};
      setGroups(data.groups || EMPTY_GROUPS);
      setColumns(data.columns || EMPTY_COLUMNS);
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      console.error('Failed to fetch grouped board:', err);
      setError(err);
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [contactEmail, linkedOrderId, statuses, enabled]);

  useEffect(() => {
    fetcher();
  }, [fetcher]);

  const refetch = useCallback(() => fetcher(), [fetcher]);

  const quotesInColumn = useCallback(
    (groupKey, status) => groups[groupKey]?.quotesByStatus?.[status] || [],
    [groups]
  );

  const allQuotesInGroup = useCallback(
    (groupKey) => {
      const byStatus = groups[groupKey]?.quotesByStatus || {};
      return Object.values(byStatus).flat();
    },
    [groups]
  );

  return {
    groups,
    columns,
    loading,
    error,
    refetch,
    quotesInColumn,
    allQuotesInGroup,
  };
}
